import EmbedFrame from "./EmbedFrame.js"
import { objectToQueryString } from "@utils/url"
import type React from "react"
import { memo, useMemo, useState, useEffect, useRef } from "react"
import { auth } from "@utils/firebase"
import { useAuthState } from "react-firebase-hooks/auth"
import HeaderProgress from "@components/Progress/HeaderProgress.js"

interface JotformPageProps {
  formId: string
  onLoad?: () => void
  onSubmit: () => void
  urlParams?: Record<string, string | number | boolean>
}

const JotformPage: React.FC<JotformPageProps> = ({ formId, onLoad, onSubmit, urlParams }) => {
  const [userAuth, userLoading] = useAuthState(auth)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submissionTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const params = useMemo(() => {
    return {
      isIframeEmbed: 1,
      session: userAuth?.uid || "",
      typesession: userAuth?.uid || "",
      ...urlParams,
    }
  }, [userAuth?.uid, urlParams])

  const paramsString = useMemo(() => objectToQueryString(params), [params])

  const handleSubmit = () => {
    if (isSubmitting) return

    console.log("JotformPage: Form submission detected")
    setIsSubmitting(true)

    // Call onSubmit immediately to ensure it's triggered
    onSubmit()

    // Set a backup timeout in case the submission process gets stuck
    submissionTimeoutRef.current = setTimeout(() => {
      console.log("JotformPage: Submission backup timeout triggered")
      setIsSubmitting(false)
    }, 5000)
  }

  // Clean up timeout on unmount or formId change
  useEffect(() => {
    return () => {
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current)
      }
    }
  }, [formId])

  if (userLoading) {
    return null
  }

  return (
    <section className="grow flex items-start md:items-center justify-center border-t-4 border-l-0 md:border-t-0 md:border-l-4 border-[#F4E34E] px-0 md:px-2 lg:px-0">
      <div className="max-w-full md:max-w-[846px] w-full md:rounded-3xl md:border md:border-pn_gray-200 overflow-hidden">
        <HeaderProgress hideSteps />

        <EmbedFrame
          onLoad={onLoad}
          className="mx-auto h-full shadow-none"
          onSubmit={handleSubmit}
          autoResize={true}
          formURL={`https://eu.jotform.com/${formId}?${paramsString}`}
        />
      </div>
    </section>
  )
}
export default memo(JotformPage)
